import { ReactNode } from 'react';

/**
 * Per-variant copy for the bulk order hub and its five SEO variants.
 *
 * The page is one component rendered for six urls; which one it is arrives as the
 * `variant` prop (undefined/`hub` for the hub). The copy here is lifted from the
 * bulk-order mockups, reusing the design blocks the page already had — paragraphs,
 * bold, links, cards — rather than any new layout.
 *
 * Only the copy differs per page. The structural blocks — the feature checklist, trust
 * signals, "Who We Supply", the mailing-label sample, and the "We serve" band — render on
 * every page, so this module carries no layout flags.
 */

export type BulkFaq = { question: string; answer: string[] };

export type BulkPopular = {
  /** The per-page "Most Popular Bulk Titles" heading. */
  heading: string;
  subtitle?: ReactNode;
  /**
   * Which product band to show. The bands are the children of the `bulk-order-templates`
   * category, fetched at runtime; their titles come from that response, nothing is
   * hardcoded. The hub (no slug) shows every child; a variant shows only the child whose
   * slug matches this one.
   */
  categorySlug?: string;
};

export type BulkVariantContent = {
  /** The last breadcrumb crumb (the hub has none beyond "Bulk Orders"). */
  breadcrumbLabel: string;
  h1: ReactNode;
  /** The hero body — the intro plus any variant prose, as one block. */
  intro: ReactNode;
  faqHeading: string;
  faqs: BulkFaq[];
  popular: BulkPopular;
};

export const BULK_VARIANTS: Record<string, BulkVariantContent> = {
  hub: {
    breadcrumbLabel: 'Bulk Orders',
    h1: 'Bulk Magazine Subscriptions',
    intro: (
      <p className="mb-5">
        The easiest way to stock your waiting room, office, school, or gift list.{' '}
        <strong className="font-semibold">Save up to 85%</strong> off cover price — over 6,000
        titles in print and digital, plus books, with free shipping on magazines and a single
        invoice for everything.
      </p>
    ),
    faqHeading: 'Common Questions About Bulk Orders',
    faqs: [
      {
        question: 'What are the best magazines for medical and dental waiting rooms?',
        answer: [
          "We provide a variety to fit every demographic — favorites include US Weekly, Men's Health, Bon Appétit, GQ, and Rolling Stone, with 1,500+ titles to choose from.",
        ],
      },
      {
        question: 'Do you supply magazines for schools, libraries, and education?',
        answer: [
          'Yes — from elementary schools to colleges. Titles for library stock, classrooms, and academic research including Ranger Rick, Smithsonian, and Popular Science.',
        ],
      },
      {
        question: 'Why should businesses subscribe to magazines for their offices?',
        answer: [
          'Keep teams current with Bloomberg Businessweek, Entrepreneur, and Fast Company — and give waiting clients something better than the clock.',
        ],
      },
      {
        question: 'Can I send bulk magazines as gifts?',
        answer: [
          'Yes — send us your recipient list and we handle the logistics, with optional courtesy lines on every cover.',
        ],
      },
      {
        question: 'Is there a minimum order for bulk magazines?',
        answer: [
          'Bulk orders start at 10 magazine subscriptions. Fewer than 10? Order directly on DiscountMags.com at regular discounted prices.',
        ],
      },
      {
        question: 'Do you offer digital magazines or books in bulk?',
        answer: [
          'Yes — many titles have digital editions, and we carry books too. Mention either in your quote request for options and pricing.',
        ],
      },
    ],
    popular: {
      heading: 'Most Popular Bulk Titles',
      subtitle: (
        <>
          Print not the right fit? Many titles are also available as{' '}
          <strong className="font-semibold">digital editions</strong> — and we carry{' '}
          <strong className="font-semibold">books</strong>, too. Mention either in your quote
          request.
        </>
      ),
    },
  },

  'waiting-rooms': {
    breadcrumbLabel: 'Waiting Rooms',
    h1: 'Waiting Room Magazine Subscriptions',
    intro: (
      <>
        <p className="mb-4">
          A waiting room with current, well-chosen magazines feels shorter than it is. Give patients{' '}
          <strong className="font-semibold">People</strong>,{' '}
          <strong className="font-semibold">Consumer Reports</strong>,{' '}
          <strong className="font-semibold">Highlights</strong>, or{' '}
          <strong className="font-semibold">Golf Digest</strong> — and make the wait part of the
          experience instead of the complaint.
        </p>
        <p className="mb-4">
          DiscountMags supplies waiting room magazines in bulk to thousands of medical offices,
          hospitals, dental practices, veterinary clinics, gyms, salons, and auto shops. Choose from
          over 1,500 waiting-room-friendly titles at up to 85% off cover price, with free shipping
          on magazines.
        </p>
        <h3 className="text-lg font-semibold mt-4 mb-2">What makes bulk ordering easier with us</h3>
        <ul className="list-disc pl-6 mb-4 flex flex-col gap-1.5">
          <li>
            One invoice for your whole selection — no juggling renewal dates across publishers
          </li>
          <li>Auto-renewals so your rack never goes stale</li>
          <li>Mix and match any titles; we&apos;ll recommend a spread for your patient demographics</li>
          <li>Net-30 terms and Purchase Orders accepted</li>
        </ul>
        <p>
          Not sure where to start? Tell us your setting — pediatric office, dental practice, vet
          clinic, salon — and we&apos;ll send a recommended title list with your quote.
        </p>
      </>
    ),
    faqHeading: 'Common Questions',
    faqs: [
      {
        question: 'How many magazines does a waiting room need?',
        answer: [
          'Most offices stock 5–15 titles with 1–3 copies each, refreshed monthly. Our minimum bulk order is 10 magazines total.',
        ],
      },
      {
        question: 'What magazines are best for a medical waiting room?',
        answer: [
          "A mix: 2–3 general interest (People, Reader's Digest), 1–2 news (Time), health titles (Men's Health, Women's Health), and Highlights if you see children.",
        ],
      },
      {
        question: 'Can you put our practice name on the covers?',
        answer: [
          'Yes — every issue can carry a courtesy line like "Courtesy of [Your Practice]" on the mailing label.',
        ],
      },
      {
        question: 'Do subscriptions renew automatically?',
        answer: ['Yes — auto-renewal keeps your waiting room stocked. Cancel or change titles anytime.'],
      },
    ],
    popular: {
      heading: 'Most Popular Waiting Room Titles',
      categorySlug: 'waiting-rooms',
    },
  },

  'medical-offices': {
    breadcrumbLabel: 'Medical Offices',
    h1: "Bulk Magazines for Doctors' Offices, Hospitals & Clinics",
    intro: (
      <>
        <p className="mb-4">
          Patients judge a practice before they ever see the doctor — and the waiting room does most
          of the talking. Current magazines signal a practice that pays attention to detail.
        </p>
        <p className="mb-4">
          DiscountMags supplies bulk magazine subscriptions to medical practices of every kind:
          family medicine, pediatrics, cardiology, psychiatry and mental-health practices,
          hospitals, urgent care, and veterinary clinics. We serve institutions from
          single-physician offices to the{' '}
          <strong className="font-semibold">U.S. Department of Veterans Affairs</strong>.
        </p>
        <h3 className="text-lg font-semibold mt-4 mb-2">For patient areas</h3>
        <p className="mb-4">
          Stock a demographic mix — People and US Weekly for broad appeal, Consumer Reports and AARP
          for older patients, Highlights and Ranger Rick for pediatric offices, calming lifestyle
          titles like Real Simple for mental-health settings.
        </p>
        <h3 className="text-lg font-semibold mt-4 mb-2">For physicians and staff</h3>
        <p className="mb-4">
          Many practices add professional and health titles — Psychology Today, Scientific American,
          Prevention — for the team and exam rooms.
        </p>
        <p>
          Every order ships free, bills on a single invoice, and can carry your practice name on
          each cover. POs accepted; Net-30 terms for hospital and group-practice procurement.
        </p>
      </>
    ),
    faqHeading: 'Common Questions',
    faqs: [
      {
        question: 'What magazines do you recommend for a pediatric office?',
        answer: [
          'Highlights, Ranger Rick, and National Geographic Kids for patients; Parents and Real Simple for the adults who bring them.',
        ],
      },
      {
        question: 'Do you work with hospitals and large medical groups?',
        answer: [
          'Yes — single invoice across departments, POs accepted, Net-30 terms, dedicated account manager.',
        ],
      },
      {
        question: 'What about psychiatry and mental-health practices?',
        answer: [
          'We supply many; calming general-interest and lifestyle titles work best, and we can tailor a list to your patient population.',
        ],
      },
      {
        question: 'Can each department get different titles on one invoice?',
        answer: ['Yes — mix any titles and quantities on one consolidated bill.'],
      },
    ],
    popular: {
      heading: 'Popular Titles for Medical Offices',
      categorySlug: 'bulk-magazines-for-doctors-offices-hospitals-and-amp-clinics',
    },
  },

  'dental-offices': {
    breadcrumbLabel: 'Dental Offices',
    h1: 'Bulk Magazines for Dental Offices',
    intro: (
      <>
        <p className="mb-4">
          Nobody loves waiting at the dentist. A good magazine rack is one of the cheapest ways to
          lower the temperature in your waiting room.
        </p>
        <p className="mb-4">
          It gives nervous patients somewhere to put their attention, families something for the
          kids, and your practice a more polished first impression.
        </p>
        <p className="mb-4">
          DiscountMags stocks dental waiting rooms nationwide at up to 85% off cover price. Since
          every demographic walks through a dental office, we recommend a spread: general interest
          (People, Reader&apos;s Digest), lifestyle and food (Bon Appétit, Real Simple), sports
          (Sports Illustrated, Golf Digest), and kids&apos; titles (Highlights — a dental-office
          classic — and National Geographic Kids).
        </p>
        <p className="mb-4">
          Many dentists and orthodontists also add industry and health titles for themselves and
          their staff.
        </p>
        <p>
          Your whole selection arrives on one invoice, ships free, renews automatically so issues
          never go stale, and can carry{' '}
          <strong className="font-semibold">&quot;Courtesy of [Your Practice]&quot;</strong> on every
          cover. Minimum bulk order is 10 magazines.
        </p>
      </>
    ),
    faqHeading: 'Common Questions',
    faqs: [
      {
        question: 'What are the best magazines for a dental waiting room?',
        answer: [
          'A mix across demographics: 2–3 general interest, a food or lifestyle title, a sports title, and Highlights for children.',
        ],
      },
      {
        question: 'How many subscriptions does a dental office need?',
        answer: [
          'Most solo practices run 6–10 titles; multi-chair and orthodontic practices 10–20. Bulk pricing starts at 10 magazines.',
        ],
      },
      {
        question: 'Can we personalize the covers?',
        answer: ['Yes — every issue can print a courtesy line with your practice name.'],
      },
    ],
    popular: {
      heading: 'Popular Titles for Dental Offices',
      categorySlug: 'popular-titles-for-dental-offices',
    },
  },

  'corporate-offices': {
    breadcrumbLabel: 'Corporate Offices',
    h1: 'Bulk Magazine Subscriptions for Offices & Corporate Gifts',
    intro: (
      <>
        <p className="mb-4">
          Whether it&apos;s the lobby, the break room, or a client&apos;s mailbox, a well-chosen
          magazine does quiet work for your company.
        </p>
        <h3 className="text-lg font-semibold mb-2">For the office</h3>
        <p className="mb-4">
          Keep your team ahead of the conversation with Bloomberg Businessweek, Forbes, Fast
          Company, INC, Fortune, and Kiplinger&apos;s. Reception areas do well with a broader mix —
          add Consumer Reports, The Atlantic, and a few lifestyle titles.
        </p>
        <h3 className="text-lg font-semibold mt-4 mb-2">For client and employee gifts</h3>
        <p className="mb-4">
          A magazine subscription is the corporate gift that keeps arriving all year. Send Golf
          Digest to golf-obsessed clients, Bon Appétit to the foodies, or choose{' '}
          <strong className="font-semibold">digital editions or books</strong> for remote teams. You
          send us the recipient list — we handle the rest, with{' '}
          <strong className="font-semibold">&quot;Courtesy of [Your Company]&quot;</strong> on every
          cover.
        </p>
        <h3 className="text-lg font-semibold mt-4 mb-2">Built for procurement</h3>
        <p className="mb-4">
          Single consolidated invoice, Net-30 terms, Purchase Orders accepted, and a dedicated
          account manager instead of a ticket queue. We supply businesses from small offices to the{' '}
          <strong className="font-semibold">U.S. State Department</strong>.
        </p>
        <p>
          Bulk orders start at 10 magazines. Get a free quote — usually within one business day.
        </p>
      </>
    ),
    faqHeading: 'Common Questions',
    faqs: [
      {
        question: 'Can you send magazines to a list of different addresses?',
        answer: [
          'Yes — for gift orders, send us your recipient list and we handle fulfillment to each address.',
        ],
      },
      {
        question: 'Do you accept Purchase Orders?',
        answer: ['Yes — POs and Net-30 terms are standard for business accounts.'],
      },
      {
        question: 'Can we brand the subscriptions?',
        answer: [
          'Every issue can print a courtesy line with your company name on the mailing label.',
        ],
      },
      {
        question: 'What business titles are most popular?',
        answer: [
          "Bloomberg Businessweek, Forbes, Fast Company, INC, Fortune, Harvard Business Review, and Kiplinger's.",
        ],
      },
    ],
    popular: {
      heading: 'Popular Business & Finance Titles',
      categorySlug: 'bulk-magazine-subscriptions-for-offices-and-amp-corporate-gifts',
    },
  },

  'school-libraries': {
    breadcrumbLabel: 'Schools & Libraries',
    h1: 'Bulk Magazine Subscriptions for Schools & Libraries',
    intro: (
      <>
        <p className="mb-4">
          From elementary classrooms to university reading rooms, magazines put current, credible,
          high-interest reading in front of students in a way textbooks can&apos;t.
        </p>
        <p className="mb-4">
          DiscountMags has a long history as a magazine supplier to educational institutions — from
          single classrooms to whole districts, and public libraries from one branch to entire
          systems.
        </p>
        <h3 className="text-lg font-semibold mt-4 mb-2">For classrooms</h3>
        <p className="mb-4">
          Ranger Rick, National Geographic Kids, and Highlights for younger readers; news titles,
          Popular Science, and Smithsonian for middle and high school — for current-events units,
          independent reading, research projects, and reluctant readers.
        </p>
        <h3 className="text-lg font-semibold mt-4 mb-2">For libraries</h3>
        <p className="mb-4">
          Round out your periodicals section at a fraction of newsstand cost — news, science,
          literary, hobbyist, and large-print titles (Reader&apos;s Digest Large Print is a
          perennial). Many titles are also available as{' '}
          <strong className="font-semibold">digital editions</strong>, and we carry{' '}
          <strong className="font-semibold">books</strong> as well. One invoice covers every branch.
        </p>
        <h3 className="text-lg font-semibold mt-4 mb-2">For universities</h3>
        <p className="mb-4">
          Departmental subscriptions, student lounges, and faculty reading — from Harvard Business
          Review to The Atlantic.
        </p>
        <h3 className="text-lg font-semibold mt-4 mb-2">Built for institutional purchasing</h3>
        <p>
          School and district POs accepted, Net-30 terms, one consolidated invoice, and a real
          account manager who knows your order.
        </p>
      </>
    ),
    faqHeading: 'Common Questions',
    faqs: [
      {
        question: 'Do you accept school and district Purchase Orders?',
        answer: ['Yes — POs and Net-30 terms are standard for educational institutions.'],
      },
      {
        question: 'What magazines work best for elementary students?',
        answer: [
          'Highlights, Ranger Rick, and National Geographic Kids are the most-ordered elementary titles.',
        ],
      },
      {
        question: 'Can different schools in our district receive different titles on one PO?',
        answer: ['Yes — mix titles, quantities, and delivery addresses on a single invoice.'],
      },
      {
        question: 'Do you offer large-print titles for libraries?',
        answer: ["Yes, including Reader's Digest Large Print."],
      },
    ],
    popular: {
      heading: 'Popular Educational Titles',
      categorySlug: 'bulk-magazine-subscriptions-for-schools-and-amp-libraries',
    },
  },
};

/** Resolve a variant slug (or undefined) to its content, falling back to the hub. */
export function resolveVariant(variant?: string | null): BulkVariantContent {
  return (variant && BULK_VARIANTS[variant]) || BULK_VARIANTS.hub;
}
